import React, {useEffect, useState} from "react";
import {useHistory, useLocation} from "react-router-dom";
import styled from "styled-components/macro";
import {
    ALL_PRODUCT_OPTIONS,
    STARTING_PRODUCT_OPTIONS,
    getCurrentVersionFromUrl,
} from "../../constants/calculatorConstants";
import {getDecodedParamsJsonOrNull, jsonToQueryParamData} from "../../utils/sharableCalculatorUtils";
import { getDistribution, getAvailableProducts, getBaselineProfile } from "../../adapters/distributionHelper";
import { reconcileProductsWithGraph } from "../../adapters/dependencyAdapter";
import Heading from "./Heading/Heading";
import AddProductSection from "./AddProductSection/AddProductSection";
import HighLevelConfig from "./HighLevelConfig/HighLevelConfig";
import RecommendationWidget from "./RecommendationWidget/RecommendationWidget";

const PageContainer = styled.div`
  min-height: 100vh;
  max-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding: 0 10px;
  display: grid;
  grid-template-columns: 4fr 3fr;
  grid-gap: 10px;
  align-items: flex-start;
  height: calc(100vh - 90px);

  @media (max-width: 900px) {
    display: block;
    height: unset;
  }
`;

const Container = styled.div`
  width: 100%;
  padding: 20px;
  margin: 20px auto;
  border: 1px solid var(--color-border);
  background: var(--color-background-secondary);
  border-radius: 3px;
  max-height: 100%;
  overflow: auto;
`;

const CalculatorForm = () => {
    const location = useLocation();
    const history = useHistory();

    const [formData, setFormData] = useState<CalculatorFormDataType>({
        highLevelParameters: {
            deploymentType: "air-eks",
            version: getCurrentVersionFromUrl(),
            airGapped: false,
            faultCount: 0,
            coreCount: 16,
            ramCount: 32,
            host: "AWS",
            externalS3: false,
            backupEnabled: false,
            isMultizoanl: false,
            aksProfile: "default",
            eksProfile: "default",
            openshiftProfile: "default",
            sharedCluster: false,
        },
        // Copies, not the shared constant objects (same rule as the reconcile push).
        products: STARTING_PRODUCT_OPTIONS.map(p => ({ ...p, properties: { ...(p.properties || {}) } })),
        explicitKeys: STARTING_PRODUCT_OPTIONS.filter(p => p.key !== 'platform').map(p => p.key),
    });

    useEffect(() => {
        if (location.search) {
            const parsedData: any = getDecodedParamsJsonOrNull(location.search || "");
            if (parsedData) {
                // Backward compat: old shared URLs may not have ramCount
                if (parsedData.highLevelParameters && !parsedData.highLevelParameters.ramCount) {
                    parsedData.highLevelParameters.ramCount = (parsedData.highLevelParameters.coreCount || 16) * 2;
                }
                // URL path is the source of truth for version — the ?data blob may
                // carry a stale version from the previous deployment if the user
                // switched versions via the dropdown.
                if (parsedData.highLevelParameters) {
                    parsedData.highLevelParameters.version = getCurrentVersionFromUrl();
                }
                // Reconcile products against the current dependency graph.
                // Without this, a URL saved before a new auto-dep was added
                // (e.g. agents → studioweb) renders and computes against the
                // stale product list, even though the modal correctly surfaces
                // the new deps — so HW silently drops their dedicated nodes.
                if (parsedData.explicitKeys && parsedData.highLevelParameters?.deploymentType) {
                    parsedData.products = reconcileProductsWithGraph(
                        parsedData.explicitKeys,
                        parsedData.products || [],
                        getDistribution(parsedData.highLevelParameters.deploymentType),
                        ALL_PRODUCT_OPTIONS,
                    );
                }
                // formData is SPARSE: a missing numeric prop means "at reference";
                // display and calc both resolve it through the effective read path,
                // so neither seeding nor floor-normalization happens on load.
                setFormData(parsedData);
            }
        }
        // only for on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const queryString = jsonToQueryParamData(formData);
        history.replace({ search: queryString });
    }, [formData, history]);



    const handleChangeHighLevelParameters = (
        highLevelParameters: HighLevelParametersInterface
    ) => {
        // Remove products not available in the new distribution
        const distribution = getDistribution(highLevelParameters.deploymentType);
        const available = getAvailableProducts(distribution);
        const explicitKeys = formData.explicitKeys.filter(k => available.has(k));
        // Auto-deps differ per distribution (e.g. studioweb has different parents
        // on Linux vs OpenShift), so re-resolve against the new graph rather
        // than just filtering the old product list.
        const products = reconcileProductsWithGraph(
            explicitKeys,
            formData.products.filter(p => available.has(p.key)),
            distribution,
            ALL_PRODUCT_OPTIONS,
        );
        // Functional update: independent state writers must not be clobbered
        // by a stale-closure copy of formData.
        setFormData(prev => ({ ...prev, highLevelParameters, explicitKeys, products }));
    };

    const handleSelectedProductsChange = (products: ProductOptionInterface[], explicitKeys: string[]) => {
        setFormData(prev => ({ ...prev, products, explicitKeys }));
    };

    return (
        <PageContainer>
            <Heading
                formData={formData}
                selectedProducts={formData.products}
                onSelectedProductsChange={handleSelectedProductsChange}
                explicitKeys={formData.explicitKeys}
            />
            <Wrapper>
                <Container>
                    <HighLevelConfig
                        highLevelParameters={formData.highLevelParameters}
                        onChangeHighLevelParameters={handleChangeHighLevelParameters}
                    />
                    <RecommendationWidget formData={formData}/>
                </Container>
                <Container>
                    <AddProductSection
                        selectedProducts={formData.products}
                        onSelectedProductsChange={handleSelectedProductsChange}
                        baselineProfile={getBaselineProfile(
                            formData.highLevelParameters.deploymentType,
                            formData.highLevelParameters,
                        )}
                        deploymentType={formData.highLevelParameters.deploymentType}
                        explicitKeys={formData.explicitKeys}
                    />
                </Container>
            </Wrapper>
        </PageContainer>
    );
};

export default CalculatorForm;
